import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

const COOKIE_CONSENT_POLL_MS = 300;
const COOKIE_CONSENT_MAX_WAIT_MS = 60000;

function getIubApi() {
  return window._iub?.cs?.api;
}

function isGdprNotApplicable() {
  const api = getIubApi();
  if (!api?.gdprApplies) return false;

  try {
    return api.gdprApplies() === false;
  } catch {
    return false;
  }
}

function hasIubPreference() {
  const api = getIubApi();
  if (!api?.getPreferenceId) return false;

  try {
    return Boolean(api.getPreferenceId());
  } catch {
    return false;
  }
}

function isElementVisible(element) {
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}

function isIubBannerVisible() {
  const selectors = ['#iubenda-cs-banner', '#iubenda-cs-popup', '.iubenda-cs-visible'];

  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element instanceof HTMLElement && isElementVisible(element)) {
      return true;
    }
  }

  return false;
}

function isCookieBannerResolved() {
  if (isGdprNotApplicable()) return true;
  return hasIubPreference();
}

function isCookieConsentReadyAfterInteraction(sawBanner) {
  if (isGdprNotApplicable()) return true;
  if (!hasIubPreference()) return false;
  return sawBanner && !isIubBannerVisible();
}

function ensureCustomerPrivacyLoaded() {
  if (window.Shopify?.customerPrivacy) return Promise.resolve();

  return new Promise((resolve) => {
    if (!window.Shopify?.loadFeatures) {
      resolve();
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      () => resolve()
    );
  });
}

/**
 * Whether dismiss timestamp may be read/written in localStorage.
 * Uses Shopify Customer Privacy (synced by CMP Insert Code), not Iubenda preference id alone.
 * Reject often leaves technical purposes "true" in Iubenda state or saleOfData in Shopify.
 *
 * @returns {boolean}
 */
function canUseDismissStorage() {
  if (!isCookieBannerResolved()) return false;

  const privacy = window.Shopify?.customerPrivacy;
  if (typeof privacy?.preferencesProcessingAllowed !== 'function') {
    return false;
  }

  try {
    return (
      privacy.preferencesProcessingAllowed() === true ||
      privacy.marketingAllowed?.() === true
    );
  } catch {
    return false;
  }
}

/**
 * Waits until the visitor has expressed a cookie preference (or GDPR does not apply).
 *
 * @param {boolean} isDesignMode
 * @returns {Promise<boolean>} false if consent never resolves (e.g. Iubenda blocked)
 */
function waitForCookieConsent(isDesignMode) {
  const hadPreferenceOnLoad = hasIubPreference();
  const bannerVisibleOnLoad = isIubBannerVisible();

  if (isDesignMode) return Promise.resolve(true);
  if (isGdprNotApplicable()) return Promise.resolve(true);

  if (hadPreferenceOnLoad && !bannerVisibleOnLoad) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let settled = false;
    let sawBanner = bannerVisibleOnLoad;

    const finish = (success) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(success);
    };

    const tryFinish = () => {
      if (isIubBannerVisible()) {
        sawBanner = true;
      }

      if (!isCookieConsentReadyAfterInteraction(sawBanner)) {
        return;
      }

      void ensureCustomerPrivacyLoaded().then(() => finish(true));
    };

    const onConsent = () => {
      tryFinish();
    };

    document.addEventListener('visitorConsentCollected', onConsent);

    const poll = setInterval(() => tryFinish(), COOKIE_CONSENT_POLL_MS);

    const timeout = setTimeout(() => finish(false), COOKIE_CONSENT_MAX_WAIT_MS);

    function cleanup() {
      document.removeEventListener('visitorConsentCollected', onConsent);
      clearInterval(poll);
      clearTimeout(timeout);
    }
  });
}

/**
 * Newsletter signup popup shown after a configurable delay.
 *
 * @extends {DialogComponent}
 */
class PopupNewsletterComponent extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);

    void this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);

    if (this.#openTimeout) {
      clearTimeout(this.#openTimeout);
      this.#openTimeout = null;
    }
  }

  /**
   * Saves dismiss state and closes the popup (close button).
   */
  dismissAndClose = () => {
    this.closeDialog();
  };

  #handleDialogClose = () => {
    if (this.#isDesignMode) return;
    this.#saveDismissed();
  };

  #handleDialogOpen = () => {
    const feedbackMessage = this.#getFeedbackMessage();
    if (feedbackMessage) {
      this.#focusElement(feedbackMessage);
      return;
    }

    this.#focusEmailField();
  };

  async #init() {
    if (this.#hasSuccessMessage()) {
      this.#saveDismissed();
      this.showDialog();
      return;
    }

    if (this.#hasErrorMessage()) {
      this.showDialog();
      return;
    }

    if (this.#wasThisFormPosted()) {
      this.#saveDismissed();
      this.showDialog();
      return;
    }

    if (!this.#shouldShow()) return;

    const consentReady = await waitForCookieConsent(this.#isDesignMode);
    if (!consentReady) return;

    this.#scheduleOpen();
  }

  #focusEmailField() {
    const emailInput = this.querySelector('[data-email-signup-email]');
    if (!(emailInput instanceof HTMLInputElement)) return;
    this.#focusElement(emailInput);
  }

  /**
   * @param {HTMLElement} element
   */
  #focusElement(element) {
    const focus = () => {
      try {
        element.focus({ focusVisible: false });
      } catch {
        element.focus();
      }
    };

    requestAnimationFrame(() => {
      focus();
      requestAnimationFrame(() => {
        focus();
        setTimeout(focus, 0);
      });
    });
  }

  #getFeedbackMessage() {
    return (
      this.querySelector('[data-email-signup-success]') ||
      this.querySelector('[data-email-signup-error]')
    );
  }

  #hasSuccessMessage() {
    return Boolean(this.querySelector('[data-email-signup-success]'));
  }

  #hasErrorMessage() {
    return Boolean(this.querySelector('[data-email-signup-error]'));
  }

  /**
   * Shopify redirects to ?customer_posted=true#section-id after a customer form submit.
   *
   * @returns {boolean}
   */
  #wasThisFormPosted() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return false;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return false;

    const hash = window.location.hash.replace(/^#/, '');
    return hash === sectionId;
  }

  get #isDesignMode() {
    return window.Shopify?.designMode === true;
  }

  #storageKey() {
    return this.dataset.storageKey || 'popup-newsletter';
  }

  #dismissDays() {
    return Number(this.dataset.dismissDays ?? 7);
  }

  #delaySeconds() {
    return Number(this.dataset.delaySeconds ?? 3);
  }

  #getDismissedAt() {
    if (!canUseDismissStorage()) return null;

    try {
      const value = localStorage.getItem(this.#storageKey());
      return value ? Number(value) : null;
    } catch {
      return null;
    }
  }

  #saveDismissed() {
    if (this.#isDesignMode) return;
    if (!canUseDismissStorage()) return;

    try {
      localStorage.setItem(this.#storageKey(), String(Date.now()));
    } catch {
      // localStorage may be unavailable
    }
  }

  #shouldShow() {
    if (this.#hasSuccessMessage()) return false;

    if (this.#isDesignMode) return true;

    const dismissedAt = this.#getDismissedAt();
    if (dismissedAt === null) return true;

    const dismissDays = this.#dismissDays();
    if (dismissDays <= 0) return true;

    return Date.now() - dismissedAt >= dismissDays * 86400000;
  }

  #scheduleOpen() {
    const delayMs = this.#isDesignMode ? 0 : this.#delaySeconds() * 1000;

    this.#openTimeout = setTimeout(() => {
      if (!this.#shouldShow() || this.refs.dialog?.open) return;
      this.showDialog();
    }, delayMs);
  }
}

if (!customElements.get('popup-newsletter-component')) {
  customElements.define('popup-newsletter-component', PopupNewsletterComponent);
}
